import {
    ReactElement,
    ReactNode,
    Suspense,
    lazy,
    ComponentType,
    useMemo,
} from 'react'
import * as React from 'react'

import {
    loadRemote,
    registerRemotes,
} from '@module-federation/enhanced/runtime'
import * as ReactJsxRuntime from 'react/jsx-runtime'
import * as ReactDom from 'react-dom'
import * as ReactRouter from 'react-router'
import * as ReactRouterDom from 'react-router-dom'

import { RouteLoadingPlaceholder } from '../routing/RouteLoadingPlaceholder'

import { RemoteModuleErrorBoundary } from './RemoteModuleErrorBoundary'

const REMOTE_ENTRIES: Record<string, string> = {
    rocket: `${ROCKET_REMOTE_URL}/mf-manifest.json`,
}

const registeredRemotes = new Set<string>()

interface ViteModuleCache {
    share: Record<string, unknown>
    remote?: Record<string, unknown>
}

// `@module-federation/vite` remotes consume shared deps through their own
// `globalThis.__mf_module_cache__` rather than the runtime share scope that a
// webpack/@module-federation/enhanced host populates. Their generated import
// shims fall back to the remote's *own* bundled copy whenever a cache slot is
// empty (`react === undefined && (react = ownReact)`), which gives the remote a
// second React whose hook dispatcher is null when this host renders it. Seeding
// the cache with this host's singletons before the remote entry evaluates makes
// the remote bind to our instances instead.
let viteShareCachePrimed = false

function primeViteRemoteShareCache(): void {
    if (viteShareCachePrimed) {
        return
    }

    const globalScope = globalThis as typeof globalThis & {
        __mf_module_cache__?: ViteModuleCache
    }
    // eslint-disable-next-line no-underscore-dangle -- external global injected by @module-federation/vite remotes
    const cache = (globalScope.__mf_module_cache__ ??= {
        share: {},
        remote: {},
    })
    cache.share ??= {}

    cache.share.react ??= React
    cache.share['react/jsx-runtime'] ??= ReactJsxRuntime
    cache.share['react-dom'] ??= ReactDom
    cache.share['react-router'] ??= ReactRouter
    cache.share['react-router-dom'] ??= ReactRouterDom

    viteShareCachePrimed = true
}

function ensureRemoteRegistered(remoteName: string): void {
    if (registeredRemotes.has(remoteName)) {
        return
    }

    const entry = REMOTE_ENTRIES[remoteName]

    if (!entry) {
        throw new Error(`Unknown remote: "${remoteName}"`)
    }

    primeViteRemoteShareCache()
    registerRemotes([{ name: remoteName, entry }])
    registeredRemotes.add(remoteName)
}

function resolveRemoteComponent(
    mod: Record<string, unknown>,
    moduleId: string,
    moduleName: string
): { default: ComponentType } {
    const candidate =
        (mod.default as ComponentType | undefined) ??
        (mod[moduleName] as ComponentType | undefined)

    if (!candidate) {
        throw new Error(
            `Remote module "${moduleId}" did not export a usable component (looked for "default" and "${moduleName}")`
        )
    }

    return { default: candidate }
}

const loading = <RouteLoadingPlaceholder />

export interface RemoteModuleProps {
    scope: string
    module: string
    fallback?: ReactElement | null
    errorFallback?: ReactNode
    componentProps?: Record<string, unknown>
}

export function RemoteModule({
    scope,
    module,
    fallback,
    errorFallback,
    componentProps = {},
}: RemoteModuleProps): ReactElement {
    const moduleName = module.replace('./', '')
    const moduleId = `${scope}/${moduleName}`
    const exportName = moduleName.split('/').pop() ?? moduleName

    const Component = useMemo(
        () =>
            lazy(async () => {
                ensureRemoteRegistered(scope)
                const mod = await loadRemote(moduleId)
                if (!mod) {
                    throw new Error(`Remote module "${moduleId}" not found`)
                }
                return resolveRemoteComponent(
                    mod as Record<string, unknown>,
                    moduleId,
                    exportName
                )
            }),
        [moduleId, scope, exportName]
    )

    return (
        <RemoteModuleErrorBoundary
            remoteName={`${scope}/${module}`}
            errorFallback={errorFallback}
        >
            <Suspense fallback={fallback === undefined ? loading : fallback}>
                <Component {...componentProps} />
            </Suspense>
        </RemoteModuleErrorBoundary>
    )
}
